import { AsyncPipe, NgClass } from '@angular/common';
import { Component, computed, inject, model, signal } from '@angular/core';
import { MatButton } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { MapFilterService, MapService, VirtualFenceService } from '@vectronic/base-map';
import { dsDtos, dsEnums, userDtos, webDtos, webEnums } from '@vectronic/openApi-admin';
import {
  AlertComponent,
  CardComponent,
  DIALOG_WIDER_TALL_CONFIG,
  ParameterViewComponent,
} from '@vectronic/ui';
import { ParameterDataService } from '@vectronic/util';
import { map, Observable, of } from 'rxjs';
import { CameraScheduleService } from '../../services/camera-schedule.service';
import {
  BeaconScheduleService,
  CommunicationScheduleService,
  ExternalSensorScheduleService,
  GpsScheduleService,
  ProximityScheduleService,
  ScheduleServices,
  SolexGpsScheduleService,
  VirtualFenceScheduleService,
} from '../../services/schedule.service';
import { ScheduleViewComponent } from '../schedule-view/schedule-view.component';

@Component({
  selector: 'rc-remote-command-confirmation-dialog',
  templateUrl: './remote-command-confirmation-dialog.component.html',
  styleUrls: ['./remote-command-confirmation-dialog.component.scss'],
  imports: [
    AlertComponent,
    CardComponent,
    MatButton,
    MatIcon,
    ParameterViewComponent,
    ScheduleViewComponent,
    NgClass,
    AsyncPipe,
  ],
  providers: [
    ParameterDataService,
    ScheduleServices,
    CameraScheduleService,
    GpsScheduleService,
    CommunicationScheduleService,
    BeaconScheduleService,
    ProximityScheduleService,
    SolexGpsScheduleService,
    ExternalSensorScheduleService,
    VirtualFenceScheduleService,
    MapFilterService,
    MapService,
    VirtualFenceService,
  ],
})
export class RemoteCommandConfirmationDialogComponent {
  parameterDataService = inject(ParameterDataService);
  scheduleServices = inject(ScheduleServices);
  virtualFenceService = inject(VirtualFenceService);
  data: {
    schedules?: dsDtos.Schedule[];
    parameterDataList?: dsDtos.ParameterData[];
    selectedCollarIds?: number[];
    parameters: dsDtos.Parameter[];
    categories: dsDtos.ParameterCategory[];
    subsystems: dsDtos.Subsystem[];
    videoFormatsOptions?: { value: number; label: string }[];
    photoFormatsOptions?: { value: number; label: string }[];
    virtualFenceConstructionRules?: dsDtos.VirtualFenceConstructionRule[];
    virtualFenceCleared?: boolean;
    isRemote?: boolean;
    isAdmin: boolean;
    isGsmSelected?: boolean;
    isPositionsLoading?: boolean;
    mapPositions?: webDtos.MapPositionsList[];
    userPreferences?: userDtos.UserPreferences;
    initialScheduleTypes?: dsEnums.ScheduleType[];
    initialHasVirtualFence?: boolean;
    collars?: Array<{ id: number; comType?: webEnums.ComType; dataPlan?: webEnums.DataPlan }>;
    userSubsystemVisibility?: boolean[];
  } = inject(MAT_DIALOG_DATA);
  dialogRef = inject(MatDialogRef<RemoteCommandConfirmationDialogComponent>);

  isFullScreen = signal(false);
  showPreview = model<boolean>(true);

  isGsmSelected$: Observable<boolean> = of(!!this.data?.isGsmSelected);
  permanentEndDate$: Observable<string> = this.isGsmSelected$.pipe(
    map((isGsm) => (isGsm ? '2038-01-01' : '2050-01-01')),
  );

  shouldShowUserVisibilityWarning = computed(() => {
    return this.data?.parameterDataList?.some((pd) => {
      const parameter = this.data.parameters?.find((p) => p.id === pd.parameterId);
      return parameter?.value === 'FLAGS' && parameter?.name === 'Visibility Flags';
    });
  });

  shouldShowAirtimeWarning = computed(() => {
    if (!this.data?.schedules?.length || !this.data?.collars?.length) {
      return false;
    }

    const selectedCollarIds = new Set(this.data?.selectedCollarIds || []);
    const selectedCollars = this.data.collars.filter((c) => selectedCollarIds.has(c.id));

    const hasIridium = selectedCollars.some((c) => c.comType === 'IRIDIUM');
    if (!hasIridium) {
      return false;
    }

    const hasLimitedDataPlan = selectedCollars.some(
      (c) => c.dataPlan === 'THREE_KBYTE' || c.dataPlan === 'POOLED_THREE_KBYTE',
    );
    if (!hasLimitedDataPlan) {
      return false;
    }

    const hasLongFixRate = this.data.schedules.some((schedule) => {
      if (!schedule.rules) {
        return false;
      }
      return schedule.rules.some((rule) => {
        if (!rule.sequences) {
          return false;
        }
        return rule.sequences.some((seq) => {
          return seq.fixRate != null && seq.fixRate * (rule.skipCount + 1) <= 3600;
        });
      });
    });
    return hasLongFixRate;
  });

  constructor() {
    this.parameterDataService.init(this.data.parameterDataList);
    this.scheduleServices.initializeSchedules(this.data.schedules, false);
    this.virtualFenceService.initialize(this.data.virtualFenceConstructionRules, true);
    if (this.data.virtualFenceCleared) {
      this.virtualFenceService.isEmptyVirtualFence.set(true);
    }
  }

  onClose() {
    this.virtualFenceService.readonly.set(false);
    this.dialogRef.close();
  }

  get joinedSelectedCollarIds() {
    return this.data?.selectedCollarIds?.join(', ');
  }

  get selectedCollarIdsAsString() {
    return this.data?.selectedCollarIds?.map((selectedCollarId) => selectedCollarId.toString());
  }

  onConfirm() {
    this.virtualFenceService.readonly.set(false);
    this.dialogRef.close(true);
  }

  onCancel() {
    this.virtualFenceService.readonly.set(false);
    this.dialogRef.close(false);
  }

  onResized(isFullScreen: boolean) {
    this.isFullScreen.set(isFullScreen);
    if (isFullScreen) {
      this.dialogRef.updateSize('100vw', '100vh');
    } else {
      this.dialogRef.updateSize(DIALOG_WIDER_TALL_CONFIG.width, DIALOG_WIDER_TALL_CONFIG.height);
    }
  }
}
